import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import {
  TriangleAlert,
  ExternalLink,
  Wrench,
  Building2,
  Landmark,
  FileText,
  Check,
} from "lucide-react";
import { useEffect, useState } from "react";
import { agentMap, toneSurface } from "@/lib/mockData";
import StatusPill from "./StatusPill";
import PriorityPill from "./PriorityPill";
import { cn } from "@/lib/utils";

const RES_ICON = {
  tool: Wrench,
  agency: Building2,
  gov: Landmark,
  doc: FileText,
};

const fmtDeadline = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

export default function TaskDrawer({ task, open, onOpenChange, onUpdate }) {
  const [checked, setChecked] = useState(() => new Set());

  // Reset checks when switching tasks — hydrate from server-side completed_steps
  useEffect(() => {
    setChecked(new Set(task?.completed_steps ?? []));
  }, [task?.id, task?.completed_steps]);

  if (!task) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full p-0 sm:max-w-[540px]" />
      </Sheet>
    );
  }

  const isHuman = task.owner === "HUMAN";
  const ownerAgent = agentMap[task.owner];
  const tone = toneSurface[ownerAgent?.tone] ?? toneSurface.neutral;
  const isDarkAvatar = ownerAgent?.tone === "dark";

  const toggleStep = (i) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      // Persist to server (fire-and-forget; optimistic UI already applied).
      const arr = Array.from(next).sort((a, b) => a - b);
      onUpdate?.(task.id, { completed_steps: arr });
      return next;
    });
  };

  const markDone = () => {
    onUpdate?.(task.id, { status: "done" });
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="task-drawer"
        className="idea-scroll w-full overflow-y-auto bg-white p-0 sm:max-w-[540px]"
      >
        {/* Hidden title for a11y */}
        <SheetTitle className="sr-only">{task.title}</SheetTitle>

        {/* Header */}
        <div className="border-b border-black/[0.04] px-7 pb-5 pt-7">
          <div className="mb-3">
            <StatusPill status={task.status} />
          </div>
          <h2
            data-testid="task-drawer-title"
            className="pr-8 text-[24px] font-extrabold leading-tight tracking-tight text-zinc-900"
          >
            {task.title}
          </h2>

          <div className="mt-4 flex items-center gap-3">
            {isHuman ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-orange-700">
                <TriangleAlert className="h-3 w-3" strokeWidth={2.6} />
                Human
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <span
                  className={cn(
                    "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-extrabold",
                    isDarkAvatar
                      ? "bg-[#0B0B0E] text-white"
                      : cn(tone.bg, "text-zinc-900"),
                  )}
                  style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)" }}
                >
                  {ownerAgent?.name?.[0]}
                </span>
                <span className="text-[12.5px] font-semibold text-zinc-800">
                  {ownerAgent?.name} · {ownerAgent?.role}
                </span>
              </span>
            )}
            <PriorityPill priority={task.priority} />
            <span className="text-[12px] font-semibold text-zinc-600">
              Due {fmtDeadline(task.deadline)}
            </span>
          </div>
        </div>

        <div className="space-y-7 px-7 py-7">
          {isHuman && (
            <div
              data-testid="task-drawer-human-callout"
              className="flex items-start gap-3 rounded-2xl border border-orange-200 bg-[#FFF7EE] p-4"
            >
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-orange-500/15 text-orange-700">
                <TriangleAlert className="h-4 w-4" strokeWidth={2.6} />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-orange-700">
                  Human action required
                </div>
                <div className="mt-0.5 text-[13.5px] font-bold text-zinc-900">
                  This task can only be completed by you.
                </div>
                <div className="mt-1 text-[12.5px] text-zinc-600">
                  The boardroom is paused on this — finish it to unblock downstream agents.
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          <section>
            <div className="eyebrow mb-2">Description</div>
            <p className="text-[13.5px] leading-relaxed text-zinc-700">
              {task.description}
            </p>
          </section>

          {/* Steps */}
          <section>
            <div className="eyebrow mb-3">Step-by-step</div>
            <ol className="space-y-2">
              {task.steps.map((s, i) => {
                const done = checked.has(i);
                return (
                  <li
                    key={i}
                    data-testid={`task-step-${i}`}
                    className={cn(
                      "flex items-start gap-3 rounded-xl border border-transparent p-2.5 transition-colors hover:border-black/[0.04] hover:bg-zinc-50",
                    )}
                  >
                    <Checkbox
                      data-testid={`task-step-${i}-check`}
                      checked={done}
                      onCheckedChange={() => toggleStep(i)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400">
                        Step {i + 1}
                      </div>
                      <div
                        className={cn(
                          "mt-0.5 text-[13px] leading-snug text-zinc-800",
                          done && "text-zinc-400 line-through",
                        )}
                      >
                        {s}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>

          {/* Resources */}
          {task.resources?.length > 0 && (
            <section>
              <div className="eyebrow mb-3">External resources</div>
              <div className="space-y-2">
                {task.resources.map((r, i) => {
                  const Icon = RES_ICON[r.kind] ?? ExternalLink;
                  return (
                    <a
                      key={i}
                      href={r.url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`task-resource-${i}`}
                      className="group flex items-center gap-3 rounded-xl border border-black/[0.04] bg-white p-3 transition-colors hover:bg-zinc-50"
                    >
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-zinc-100 text-zinc-700">
                        <Icon className="h-4 w-4" strokeWidth={2.2} />
                      </div>
                      <div className="flex-1 truncate">
                        <div className="truncate text-[13px] font-semibold text-zinc-900">
                          {r.label}
                        </div>
                        <div className="truncate text-[11px] text-zinc-400">
                          {r.url}
                        </div>
                      </div>
                      <ExternalLink
                        className="h-3.5 w-3.5 text-zinc-400 transition-colors group-hover:text-zinc-700"
                        strokeWidth={2.4}
                      />
                    </a>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 border-t border-black/[0.04] bg-white px-7 py-4">
          <button
            type="button"
            data-testid="task-drawer-mark-done"
            onClick={markDone}
            disabled={task.status === "done"}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-black px-5 py-3 text-[13px] font-semibold text-white transition-colors hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Check className="h-4 w-4" strokeWidth={2.6} />
            {task.status === "done" ? "Already done" : "Mark as done"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
